/**
 * DLP frontend deterrent layer (#117).
 *
 * Mounted once at the AppShell root. Owns three behaviours:
 *
 *   1. Print gate: intercepts Ctrl+P / Cmd+P with a confirmation alert,
 *      logs the event, and (whether continued or cancelled) lets the
 *      browser-level @media print CSS swap content for a "restricted"
 *      notice. Also catches the menu-driven Print path via
 *      window.onbeforeprint.
 *
 *   2. Right-click suppression: prevents the default browser context
 *      menu across the entire authenticated shell (same scope as the
 *      watermark) and shows a centered alert.
 *
 *   3. Save-as keyboard interception: blocks Ctrl+S and shows a
 *      centered alert.
 *
 * Pedro 2026-05-04 round-2 feedback applied:
 *   - All three warnings rendered as centered orange filled Alerts via
 *     a single body-portal — same shape, palette, position. The print
 *     confirmation has Continue/Cancel buttons inline.
 *   - Right-click no longer gated by PII path: previously gated by
 *     isPiiPath() which excluded /audit, list pages and the dashboard;
 *     all of those still expose extraction vectors via the browser
 *     context menu (View source, Copy, Save image as, Inspect).
 *     Broader scope matches the watermark.
 *   - Message copy reworked at Pedro's request to a "To protect client
 *     data… Should you need to extract client data, please speak to
 *     Compliance. This event has been logged with your username and
 *     timestamp." pattern across all three warnings.
 *
 * Pedro 2026-05-03 round-1 feedback (already applied):
 *   - Replaced Mantine top-center notifications with the custom
 *     centered alert host.
 *   - Removed blur surveillance entirely (Alt+Tab false positives
 *     defeat the deterrent and the audit value).
 *   - Modal copy "will be" → "has been" logged.
 *   - contextmenu listener moved to window-level with capture phase.
 *
 * Charter framing unchanged: any layer here is defeatable in 30 s by
 * a user with DevTools. The value is friction + audit + attribution,
 * not prevention.
 */
import { Alert } from '@mantine/core';
import { IconShieldLock } from '@tabler/icons-react';
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

import { dlpApi } from '../api/dlp';

// Cooldown (ms) between alerts of the same kind. Server-side has its
// own 30-per-5min cap; this client cooldown is a UX guardrail against
// legitimate users seeing the same scolding three times in a row.
const ALERT_AUTO_DISMISS_MS = 6_000;

interface ActiveAlert {
  id: number;
  title: string;
  message: string;
  icon: ReactNode;
}

export function DlpProtection() {
  const [alerts, setAlerts] = useState<ActiveAlert[]>([]);
  const alertSeqRef = useRef(0);

  const dismissAlert = useCallback((id: number) => {
    setAlerts(prev => prev.filter(a => a.id !== id));
  }, []);

  const pushAlert = useCallback((title: string, message: string, icon: ReactNode) => {
    const id = ++alertSeqRef.current;
    setAlerts(prev => [...prev, { id, title, message, icon }]);
    window.setTimeout(() => dismissAlert(id), ALERT_AUTO_DISMISS_MS);
  }, [dismissAlert]);

  // Pedro 2026-05-04 round-4: every DLP event fires a log AND a toast
  // unconditionally. No throttle. Reasoning: (a) repeated reminders
  // are exactly what a deterrent layer is for; if the user is rapidly
  // pressing Ctrl+S or right-clicking, they're behaving in the way
  // the deterrent should reach; (b) the previous toast throttle was
  // a UX nicety borrowed from notification UX, but DLP isn't a
  // notification — it's a friction layer. (c) The server-side cap of
  // 30 signals per 5 min still bounds runaway logs.

  // ─── 1. PRINT EVENTS — menu-driven path ──────────────────────────
  // Browser menu File → Print bypasses our keyboard handler. When the
  // print preview opens, onbeforeprint fires; we can't pop an alert
  // (the OS dialog is already on top) but we can log the attempt and
  // the @media print CSS in dlp.css replaces the body content with
  // the "restricted" notice. The afterprint event fires if the user
  // actually completes the print.
  useEffect(() => {
    const onBeforePrint = () => {
      dlpApi.recordEvent({
        event_type: 'print_attempt',
        page_path: window.location.pathname,
        extra: { trigger: 'menu_or_unknown' },
      });
    };
    const onAfterPrint = () => {
      dlpApi.recordEvent({
        event_type: 'print_completed',
        page_path: window.location.pathname,
      });
    };
    window.addEventListener('beforeprint', onBeforePrint);
    window.addEventListener('afterprint', onAfterPrint);
    return () => {
      window.removeEventListener('beforeprint', onBeforePrint);
      window.removeEventListener('afterprint', onAfterPrint);
    };
  }, []);

  // ─── 2. KEYBOARD: Ctrl+P, Ctrl+S ──────────────────────────────────
  // Pedro 2026-05-04 round-4: Ctrl+P is now a hard block, not a
  // confirmation gate. The earlier Continue/Cancel modal was internally
  // inconsistent with a "not permitted" policy — it implied "well, you
  // *can* if you really want to." Now Ctrl+P matches Ctrl+S exactly:
  // log + toast, no print preview opens, full stop. Anyone who needs
  // legitimate extraction goes through Compliance.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const ctrl = e.ctrlKey || e.metaKey;
      if (!ctrl) return;
      if (e.key === 'p' || e.key === 'P') {
        e.preventDefault();
        dlpApi.recordEvent({
          event_type: 'print_attempt',
          page_path: window.location.pathname,
          extra: { trigger: 'keyboard_ctrl_p' },
        });
        pushAlert(
          'Print blocked',
          'To protect client data, printing this page is not permitted. '
          + 'Should you need to extract client data, please speak to '
          + 'Compliance. This event has been logged with your username '
          + 'and timestamp.',
          <IconShieldLock size={20} />,
        );
      } else if (e.key === 's' || e.key === 'S') {
        e.preventDefault();
        dlpApi.recordEvent({
          event_type: 'save_blocked',
          page_path: window.location.pathname,
          extra: { trigger: 'keyboard_ctrl_s' },
        });
        pushAlert(
          'Save blocked',
          'To protect client data, saving this page is not permitted. '
          + 'Should you need to extract client data, please speak to '
          + 'Compliance. This event has been logged with your username '
          + 'and timestamp.',
          <IconShieldLock size={20} />,
        );
      }
    };
    // Capture phase + window scope so we always intercept first.
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [pushAlert]);

  // ─── 3. CONTEXT MENU ──────────────────────────────────────────────
  // Window-level capture, attached unconditionally. Disables the
  // browser's right-click menu across the entire authenticated shell
  // — same scope as the watermark — so list pages, dashboards, and
  // the audit page are also covered. Pedro 2026-05-04 round-2:
  // previous implementation gated by isPiiPath(), which let
  // /audit, list pages, and the dashboard through. The browser menu
  // exposes "View source", "Save image as", "Inspect", "Copy" — all
  // useful for casual extraction — so the broader scope matches the
  // DLP intent.
  useEffect(() => {
    const onContext = (e: MouseEvent) => {
      // Block, log, and toast — all every time. Per round-4: deterrent
      // layers are *for* repeated attempts, throttling them undermines
      // the whole point.
      e.preventDefault();
      e.stopPropagation();
      dlpApi.recordEvent({
        event_type: 'context_menu_blocked',
        page_path: window.location.pathname,
      });
      pushAlert(
        'Right-click disabled',
        'To protect client data, the right-click menu is disabled. '
        + 'Should you need to extract client data, please speak to '
        + 'Compliance. This event has been logged with your username '
        + 'and timestamp.',
        <IconShieldLock size={20} />,
      );
    };
    window.addEventListener('contextmenu', onContext, true);
    return () => window.removeEventListener('contextmenu', onContext, true);
  }, [pushAlert]);

  // (No blur surveillance. Pedro 2026-05-03: Alt+Tab is common and
  // mostly innocent; false positives defeat the purpose of even
  // logging because Compliance can't act on it without presumption-
  // of-guilt risk. Removed entirely from this layer.)

  // Single rendered host: portals to <body>, centered, holds every
  // active DLP alert. After round-4 every warning (Ctrl+P, Ctrl+S,
  // right-click) is a uniform notification: filled orange Alert,
  // X-button to dismiss, auto-dismiss after ALERT_AUTO_DISMISS_MS,
  // stacks vertically when multiple are active.
  if (alerts.length === 0) return null;

  return createPortal(
    <div
      style={{
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: 10000,
        // The host div itself shouldn't capture clicks — only the
        // alerts inside it should.
        pointerEvents: 'none',
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        width: 'min(520px, 92vw)',
      }}
    >
      {alerts.map((a) => (
        <Alert
          key={a.id}
          color="orange"
          variant="filled"
          icon={a.icon}
          title={a.title}
          withCloseButton
          onClose={() => dismissAlert(a.id)}
          styles={{
            root: {
              pointerEvents: 'auto',
              boxShadow: '0 16px 48px rgba(0,0,0,0.35)',
            },
          }}
        >
          {a.message}
        </Alert>
      ))}
    </div>,
    document.body,
  );
}
